import React, { useState } from "react";
import { api } from "@/lib/api";
import { X, FileText, Loader2, Truck, AlertTriangle, Check, Camera } from "lucide-react";
import { SignatureUploadModal } from "@/pages/Waybills";

async function downloadFromResponse(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function WaybillModal({ open, container, onClose, onIssued }) {
  const [form, setForm] = useState({
    client: "Hapag-Lloyd",
    shipping_line: "Hapag-Lloyd",
    carrier: "",
    truck_plate: "",
    driver_name: "",
    driver_license: "",
    destination: "",
    seal_no: "",
    vessel: "",
    booking_ref: "",
    tare_kg: "",
    gross_kg: "",
    condition: "SOUND",
    movement: "GATE OUT",
    comments: "",
  });
  const [issuing, setIssuing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [issuedWaybill, setIssuedWaybill] = useState(null);
  const [showSigUpload, setShowSigUpload] = useState(false);

  if (!open || !container) return null;

  const set = (k) => (e) => {
    let v = e?.target?.value ?? e;
    if (["truck_plate", "seal_no", "driver_license", "booking_ref"].includes(k)) {
      v = (v || "").toUpperCase();
    }
    setForm((s) => ({ ...s, [k]: v }));
  };

  const payload = () => ({
    container_id: container.id,
    container_no: container.container_no,
    type_size: container.type_size,
    inspection_date: container.date,
    ...form,
  });

  const previewDraft = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const r = await api.post("/waybills/preview", payload(), { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const issue = async () => {
    if (!form.truck_plate.trim()) { setError("Truck plate is required to issue a waybill"); return; }
    if (!form.driver_name.trim()) { setError("Driver name is required to issue a waybill"); return; }
    setIssuing(true);
    setError(null);
    try {
      const created = await api.post("/waybills", payload());
      const wbNo = created.data.waybill_no;
      const r = await api.get(`/waybills/${wbNo}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      await downloadFromResponse(blob, `${wbNo}.pdf`);
      onIssued?.(created.data);
      setIssuedWaybill(created.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to issue waybill");
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-3 sm:p-4 overflow-y-auto" data-testid="waybill-modal">
      <div className="bg-white w-full max-w-3xl rounded-md border border-slate-200 shadow-2xl my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0a2952]/10 grid place-items-center">
              <FileText className="text-[#0a2952]" size={18} />
            </div>
            <div>
              <div className="overline">Generate waybill</div>
              <div className="font-display font-extrabold text-lg text-[#0a2952] leading-tight">
                <span className="font-mono-tabular">{container.container_no}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-sm font-normal">{container.type_size}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-sm font-normal">{container.date}</span>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="waybill-close-btn"
            className="w-9 h-9 grid place-items-center rounded text-slate-500 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {issuedWaybill && (
            <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-md p-4" data-testid="wb-issued-banner">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#22c55e] grid place-items-center flex-shrink-0">
                  <Check className="text-white" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#15803d]">
                    Waybill <span className="font-mono-tabular">{issuedWaybill.waybill_no}</span> issued & downloaded
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Hand the printed page to the driver. Once they sign, capture a photo here to lock the proof.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setShowSigUpload(true)} data-testid="wb-capture-now-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-[#ff5b2c] hover:bg-[#e54f24] text-white text-xs font-bold uppercase tracking-wide">
                      <Camera size={13} /> Capture signature now
                    </button>
                    <button type="button" onClick={onClose} data-testid="wb-close-after-issue-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-slate-300 text-slate-700 text-xs font-bold uppercase tracking-wide hover:bg-white">
                      Later — close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Section title="Shipment">
            <Grid>
              <Field label="Movement">
                <select value={form.movement} onChange={set("movement")} data-testid="wb-movement"
                  className="form-input bg-white">
                  <option value="GATE OUT">GATE OUT</option>
                  <option value="GATE IN">GATE IN</option>
                </select>
              </Field>
              <Field label="Condition">
                <div className="grid grid-cols-2 gap-2">
                  {["SOUND", "DAMAGED"].map((c) => (
                    <button key={c} type="button" onClick={() => set("condition")(c)} data-testid={`wb-condition-${c}`}
                      className={`px-3 py-2 rounded-md font-bold text-xs uppercase tracking-wider transition-all ${
                        form.condition === c
                          ? (c === "SOUND" ? "bg-[#22c55e] text-white shadow" : "bg-red-600 text-white shadow")
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}>
                      {c}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Client">
                <input type="text" value={form.client} onChange={set("client")} data-testid="wb-client" className="form-input" placeholder="Hapag-Lloyd" />
              </Field>
              <Field label="Shipping line">
                <input type="text" value={form.shipping_line} onChange={set("shipping_line")} data-testid="wb-shipping-line" className="form-input" />
              </Field>
              <Field label="Destination" wide>
                <input type="text" value={form.destination} onChange={set("destination")} data-testid="wb-destination"
                  className="form-input" placeholder="Shed 6, GPHA Tema · Customer yard · etc." />
              </Field>
            </Grid>
          </Section>

          <Section title="Truck & Driver" required>
            <Grid>
              <Field label="Trucking company">
                <input type="text" value={form.carrier} onChange={set("carrier")} data-testid="wb-carrier" className="form-input" placeholder="e.g. Bonus Carriers Ltd" />
              </Field>
              <Field label="Truck plate *">
                <input type="text" value={form.truck_plate} onChange={set("truck_plate")} data-testid="wb-truck-plate"
                  className="form-input font-mono-tabular" placeholder="GT 8490-22" />
              </Field>
              <Field label="Driver name *">
                <input type="text" value={form.driver_name} onChange={set("driver_name")} data-testid="wb-driver-name" className="form-input" placeholder="Kwame Mensah" />
              </Field>
              <Field label="Driver license / ID">
                <input type="text" value={form.driver_license} onChange={set("driver_license")} data-testid="wb-driver-license" className="form-input font-mono-tabular" placeholder="DRV-..." />
              </Field>
            </Grid>
          </Section>

          <Section title="Container details (optional)">
            <Grid>
              <Field label="Seal no.">
                <input type="text" value={form.seal_no} onChange={set("seal_no")} data-testid="wb-seal" className="form-input font-mono-tabular" />
              </Field>
              <Field label="Booking ref">
                <input type="text" value={form.booking_ref} onChange={set("booking_ref")} data-testid="wb-booking" className="form-input font-mono-tabular" />
              </Field>
              <Field label="Vessel">
                <input type="text" value={form.vessel} onChange={set("vessel")} data-testid="wb-vessel" className="form-input" />
              </Field>
              <Field label="Tare / Gross (kg)">
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" value={form.tare_kg} onChange={set("tare_kg")} data-testid="wb-tare" className="form-input font-mono-tabular" placeholder="Tare" />
                  <input type="text" value={form.gross_kg} onChange={set("gross_kg")} data-testid="wb-gross" className="form-input font-mono-tabular" placeholder="Gross" />
                </div>
              </Field>
              <Field label="Comments" wide>
                <textarea rows={2} value={form.comments} onChange={set("comments")} data-testid="wb-comments"
                  className="form-input resize-none" placeholder="Any special notes for the driver..." />
              </Field>
            </Grid>
          </Section>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2" data-testid="wb-error">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500 max-w-md">
            A waybill number is reserved only when you click <strong>Issue & Download</strong>. Preview opens a draft in a new tab.
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={previewDraft} disabled={previewing || issuing || !!issuedWaybill}
              data-testid="wb-preview-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-300 text-sm font-semibold text-[#0a2952] hover:border-[#0a2952] disabled:opacity-60">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {previewing ? "Building…" : "Preview draft"}
            </button>
            <button type="button" onClick={issue} disabled={issuing || previewing || !!issuedWaybill}
              data-testid="wb-issue-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#ff5b2c] hover:bg-[#e54f24] text-sm font-bold text-white shadow disabled:opacity-60">
              {issuing ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
              {issuing ? "Issuing…" : issuedWaybill ? "Issued ✓" : "Issue & Download"}
            </button>
          </div>
        </div>
      </div>

      <SignatureUploadModal
        open={showSigUpload}
        waybill={issuedWaybill}
        onClose={() => setShowSigUpload(false)}
        onSuccess={() => { setShowSigUpload(false); onClose?.(); }}
      />
    </div>
  );
}

function Section({ title, required, children }) {
  return (
    <div>
      <div className="overline mb-2 flex items-center gap-2">
        {title}{required && <span className="text-[#ff5b2c]">*</span>}
      </div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children, wide }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
